import { z } from 'zod';

/**
 * `pending-pr-preview-runs` v0 — wire types for the rich PR-comment
 * composer. v0 ships the composer + read-only API + DB scaffolding;
 * v0.1 wires GitHub delivery (octokit POST + PATCH); v0.3 widens
 * delivery to GitLab + Bitbucket and adds inline visual-diff thumbnails
 * to the comment body (locked decisions §11-12).
 *
 * Locked decisions (2026-05-28): GitHub-only in v0, edit-in-place
 * via `pr_runs.comment_id`, TIA-subset cap 50, dedicated
 * `ai.prPreviewProvider` routing slot.
 * Locked decisions (2026-05-30): GitLab uses per-org PAT, Bitbucket
 * Cloud uses per-org Workspace/Repository Access Token, visual-diff
 * thumbnails are linked Markdown URLs capped at top-3 (NOT data: URIs).
 */

export const PR_RUN_STATUSES = ['pending', 'running', 'complete', 'errored', 'superseded'] as const;
export type PrRunStatus = (typeof PR_RUN_STATUSES)[number];

export const VCS_PROVIDERS = ['github', 'gitlab', 'bitbucket'] as const;
export type VcsProvider = (typeof VCS_PROVIDERS)[number];

export const PrRunSummaryTileSchema = z.object({
  kind: z.string(),
  state: z.enum(['green', 'amber', 'red', 'na']),
  headline: z.string().nullable(),
  detail: z.string(),
});
export type PrRunSummaryTile = z.infer<typeof PrRunSummaryTileSchema>;

/**
 * `pending-pr-preview-runs` v0.3 — one row in the inline visual-diff
 * thumbnail table that the composer drops into the PR comment markdown.
 *
 * Sourced from a test run's `step_results[i].visualCheck` payload for
 * each step whose `status === 'mismatch'`. The composer caps the
 * embedded set at the top-3 by `matchPercentage` (smallest match first)
 * so the rendered comment stays under GitHub's 65KB body limit and
 * doesn't drown a reviewer in screenshots. Older diffs link out to the
 * Rynora flow report.
 *
 * URLs are NOT base64-encoded data: URIs (locked decision §12):
 *  - GitHub silently strips `data:` schemes in Markdown image tags.
 *  - GitLab / Bitbucket render them inconsistently.
 *  - A single 720p screenshot's base64 is ~400KB — three of them blow
 *    past GitHub's 65KB body limit.
 *
 * `diffUrl` is nullable because some screenshot renderers don't emit a
 * separate diff PNG (only baseline + actual). The composer degrades
 * gracefully — if `diffUrl` is null the row shows only the baseline +
 * actual columns.
 */
export const PrRunVisualDiffSchema = z.object({
  stepTitle: z.string(),
  matchPercentage: z.number().min(0).max(100),
  baselineUrl: z.string(),
  actualUrl: z.string(),
  diffUrl: z.string().nullable(),
});
export type PrRunVisualDiff = z.infer<typeof PrRunVisualDiffSchema>;

export const PrRunPayloadSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  projectId: z.string(),
  vcsProvider: z.enum(VCS_PROVIDERS),
  repoSlug: z.string(),
  prNumber: z.number().int().positive(),
  baseSha: z.string(),
  headSha: z.string(),
  previewUrl: z.string().nullable(),
  commentId: z.string().nullable(),
  status: z.enum(PR_RUN_STATUSES),
  /** Composed payload mirroring release_verdicts.signals shape. */
  summary: z.object({
    verdict: z.enum(['green', 'amber', 'red']),
    tiles: z.array(PrRunSummaryTileSchema),
    llmSummary: z.string().nullable(),
    llmProvider: z.string().nullable(),
    flowsRan: z.number().int().nonnegative(),
    flowsPassed: z.number().int().nonnegative(),
    flowsFailed: z.number().int().nonnegative(),
    visualDiffsPending: z.number().int().nonnegative(),
    coverageOfChangedFiles: z.number().min(0).max(1).nullable(),
    /**
     * v0.3 — the top-3 mismatched visual-diff rows that were embedded
     * inline in the rendered Markdown. Older deployments without the
     * field default to `[]` on read (the route layer fills the gap so
     * pre-v0.3 rows round-trip without a backfill migration).
     */
    visualDiffs: z.array(PrRunVisualDiffSchema).default([]),
  }),
  renderedMarkdown: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PrRunPayload = z.infer<typeof PrRunPayloadSchema>;
